import { paths } from 'src/routes/paths';

import { POLICY } from 'src/utils/constants';
import { hasIncidentFeatures } from 'src/utils/helper';
import { Iconify } from 'src/components/iconify';
// ----------------------------------------------------------------------

// Topic-specific icons rendered via Iconify (solar duotone set).
const navIcon = (name) => <Iconify icon={name} width={24} />;

// ----------------------------------------------------------------------

// Top-level items are grouped into categories via `subheader`. The vertical nav
// renders each subheader as a collapsible header; the mini and horizontal navs
// ignore the label and just keep the grouping order.
//
// Each leaf item carries the `policies` its corresponding backend GET endpoint
// requires (mirrors `.RequireAuthorization(...)`). Items with no `policies` are
// visible to any authenticated user. Parent items need none — they are dropped
// automatically when all their children are filtered out, and a whole category
// disappears once it has no visible items
// (see `filterNavByAccess` in src/layouts/dashboard/layout.jsx).
export const navData = [
  {
    subheader: 'Catalog',
    items: [
      {
        title: 'Games',
        path: paths.dashboard.gameConfiguration.list,
        icon: navIcon('solar:gamepad-bold-duotone'),
        policies: [POLICY.GAME_CONF_GET],
      },
      {
        title: 'External Games',
        path: paths.dashboard.gameConfiguration.external,
        icon: navIcon('solar:link-bold-duotone'),
        policies: [POLICY.GAME_CONF_GET_EXT],
      },
      {
        title: 'Features',
        path: paths.dashboard.featureConfiguration.root,
        icon: navIcon('solar:magic-stick-3-bold-duotone'),
        children: [
          {
            title: 'Bonus Buys',
            path: paths.dashboard.featureConfiguration.bonusBuy,
            policies: [POLICY.GAME_CONF_GET],
            icon: navIcon('solar:cart-large-2-bold-duotone'),
          },
          {
            title: 'Ante Bets',
            path: paths.dashboard.featureConfiguration.anteBet,
            policies: [POLICY.GAME_CONF_GET],
            icon: navIcon('solar:wad-of-money-bold-duotone'),
          },
        ],
      },
      {
        title: 'Engines',
        path: paths.dashboard.engineManagement.list,
        icon: navIcon('solar:cpu-bolt-bold-duotone'),
        policies: [POLICY.ENGINES_GET],
      },
    ],
  },
  {
    subheader: 'Operators & Bonuses',
    items: [
      {
        title: 'Operators',
        path: paths.dashboard.operators.list,
        icon: navIcon('solar:buildings-2-bold-duotone'),
        policies: [POLICY.OPERATOR_ADMIN],
      },
      {
        title: 'Bonuses',
        path: paths.dashboard.bonuses.list,
        icon: navIcon('solar:gift-bold-duotone'),
        policies: [POLICY.GAME_BONUS_GET],
      },
      {
        title: 'User Bonuses',
        path: paths.dashboard.bonuses.userBonuses,
        icon: navIcon('solar:hand-money-bold-duotone'),
        policies: [POLICY.GAME_BONUS_GET],
      },
    ],
  },
  {
    subheader: 'Client Delivery',
    items: [
      {
        title: 'Clients',
        path: paths.dashboard.clientConfiguration.list,
        icon: navIcon('solar:user-id-bold-duotone'),
        policies: [POLICY.CLIENT_CONF_GET],
      },
      {
        title: 'Loader',
        path: paths.dashboard.clientConfiguration.loader,
        icon: navIcon('solar:cloud-upload-bold-duotone'),
        policies: [POLICY.PACKAGES_GET],
      },
      {
        title: 'Select Page',
        path: paths.dashboard.selectPageConfiguration.root,
        icon: navIcon('solar:widget-5-bold-duotone'),
        policies: [POLICY.SELECT_PAGE_GET],
      },
    ],
  },
  {
    subheader: 'Jackpots',
    items: [
      // One page holds pools, jackpots and groups. Any of the three read policies gets you in — the
      // page itself decides which sections that policy can see.
      {
        title: 'Jackpots',
        path: paths.dashboard.jackpotConfiguration.root,
        icon: navIcon('solar:cup-star-bold-duotone'),
        policies: [POLICY.JACKPOT_GET, POLICY.JACKPOT_GROUP_GET, POLICY.LIQUIDITY_POOL_GET],
      },
    ],
  },
  {
    subheader: 'Player Activity',
    items: [
      {
        title: 'Investigation',
        path: paths.dashboard.investigation.list,
        icon: navIcon('solar:bug-bold-duotone'),
        policies: [POLICY.HISTORY_GET],
      },
      ...(hasIncidentFeatures()
        ? [
            {
              title: 'Incidents',
              path: paths.dashboard.incidents.list,
              icon: navIcon('solar:letter-bold-duotone'),
              policies: [POLICY.INCIDENT_GET, POLICY.INCIDENT_CREATE_UPDATE],
            },
          ]
        : []),
    ],
  },
  {
    subheader: 'Testing & Quality',
    items: [
      {
        title: 'RTP Verification',
        path: paths.dashboard.testing.rtpVerification.list,
        icon: navIcon('solar:test-tube-bold-duotone'),
        policies: [POLICY.RTP_VERIFICATION_GET],
      },
      {
        title: 'RTP Verification Form Builder',
        path: paths.dashboard.testing.formBuilder.list,
        icon: navIcon('solar:document-add-bold-duotone'),
        policies: [POLICY.RTP_VERIFICATION_GET],
      },
      {
        title: 'Load Testing',
        path: paths.dashboard.testing.load.list,
        icon: navIcon('solar:graph-up-bold-duotone'),
        policies: [POLICY.GAME_ENGINE_LOAD_TEST_GET],
      },
      {
        title: 'Game Simulation',
        path: paths.dashboard.testing.gameSimulation.list,
        icon: navIcon('solar:gamepad-charge-bold-duotone'),
        policies: [POLICY.GAME_SIMULATION_GET],
      },
      {
        title: 'Callback Tool',
        path: paths.dashboard.testing.callbackTool.root,
        icon: navIcon('solar:transfer-horizontal-bold-duotone'),
        policies: [POLICY.PROVIDER_INTEGRATION_CALLBACK_CREATE_UPDATE],
      },
      {
        title: 'Game Event Logger',
        path: paths.dashboard.testing.gameEventLogger.list,
        icon: navIcon('solar:clipboard-list-bold-duotone'),
        policies: [POLICY.GAME_EVENT_LOGGER_GET],
      },
    ],
  },
  {
    subheader: 'Administration',
    items: [
      {
        title: 'Users',
        path: paths.dashboard.user.list,
        icon: navIcon('solar:user-rounded-bold-duotone'),
        policies: [POLICY.USERS_GET],
      },
      {
        title: 'Roles',
        path: paths.dashboard.user.roles,
        icon: navIcon('solar:shield-keyhole-bold-duotone'),
        policies: [POLICY.ROLE_GET],
      },
      {
        title: 'Dictionaries',
        path: paths.dashboard.dictionaries.root,
        icon: navIcon('solar:book-2-bold-duotone'),
        policies: [POLICY.DICTIONARIES_GET],
      },
      {
        title: 'Audit',
        path: paths.dashboard.audit.list,
        icon: navIcon('solar:shield-check-bold-duotone'),
        policies: [POLICY.AUDIT_GET],
      },
    ],
  },
];

//OLD!!!!
// export const navDataDEMO = [
//   /**
//    * Overview
//    */
//   {
//     subheader: 'Overview',
//     items: [
//       { title: 'App', path: demoPaths.dashboard.root, icon: ICONS.dashboard },
//       { title: 'Ecommerce', path: demoPaths.dashboard.general.ecommerce, icon: ICONS.ecommerce },
//       { title: 'Analytics', path: demoPaths.dashboard.general.analytics, icon: ICONS.analytics },
//       { title: 'Banking', path: demoPaths.dashboard.general.banking, icon: ICONS.banking },
//       { title: 'Booking', path: demoPaths.dashboard.general.booking, icon: ICONS.booking },
//       { title: 'File', path: demoPaths.dashboard.general.file, icon: ICONS.file },
//       { title: 'Course', path: demoPaths.dashboard.general.course, icon: ICONS.course },
//     ],
//   },
//   /**
//    * Management
//    */
//   {
//     subheader: 'Management',
//     items: [
//       {
//         title: 'User Management',
//         path: demoPaths.dashboard.user.root,
//         icon: ICONS.user,
//         children: [
//           { title: 'User List', path: demoPaths.dashboard.user.list },
//           { title: 'Roles List', path: demoPaths.dashboard.user.roles },
//         ],
//       },
//       {
//         title: 'Product',
//         path: demoPaths.dashboard.product.root,
//         icon: ICONS.product,
//         children: [
//           { title: 'List', path: demoPaths.dashboard.product.root },
//           { title: 'Details', path: demoPaths.dashboard.product.demo.details },
//           { title: 'Create', path: demoPaths.dashboard.product.new },
//           { title: 'Edit', path: demoPaths.dashboard.product.demo.edit },
//         ],
//       },
//       {
//         title: 'Order',
//         path: demoPaths.dashboard.order.root,
//         icon: ICONS.order,
//         children: [
//           { title: 'List', path: demoPaths.dashboard.order.root },
//           { title: 'Details', path: demoPaths.dashboard.order.demo.details },
//         ],
//       },
//       {
//         title: 'Invoice',
//         path: demoPaths.dashboard.invoice.root,
//         icon: ICONS.invoice,
//         children: [
//           { title: 'List', path: demoPaths.dashboard.invoice.root },
//           { title: 'Details', path: demoPaths.dashboard.invoice.demo.details },
//           { title: 'Create', path: demoPaths.dashboard.invoice.new },
//           { title: 'Edit', path: demoPaths.dashboard.invoice.demo.edit },
//         ],
//       },
//       {
//         title: 'Blog',
//         path: demoPaths.dashboard.post.root,
//         icon: ICONS.blog,
//         children: [
//           { title: 'List', path: demoPaths.dashboard.post.root },
//           { title: 'Details', path: demoPaths.dashboard.post.demo.details },
//           { title: 'Create', path: demoPaths.dashboard.post.new },
//           { title: 'Edit', path: demoPaths.dashboard.post.demo.edit },
//         ],
//       },
//       {
//         title: 'Job',
//         path: demoPaths.dashboard.job.root,
//         icon: ICONS.job,
//         children: [
//           { title: 'List', path: demoPaths.dashboard.job.root },
//           { title: 'Details', path: demoPaths.dashboard.job.demo.details },
//           { title: 'Create', path: demoPaths.dashboard.job.new },
//           { title: 'Edit', path: demoPaths.dashboard.job.demo.edit },
//         ],
//       },
//       {
//         title: 'Tour',
//         path: demoPaths.dashboard.tour.root,
//         icon: ICONS.tour,
//         children: [
//           { title: 'List', path: demoPaths.dashboard.tour.root },
//           { title: 'Details', path: demoPaths.dashboard.tour.demo.details },
//           { title: 'Create', path: demoPaths.dashboard.tour.new },
//           { title: 'Edit', path: demoPaths.dashboard.tour.demo.edit },
//         ],
//       },
//       { title: 'File manager', path: demoPaths.dashboard.fileManager, icon: ICONS.folder },
//       {
//         title: 'Mail',
//         path: demoPaths.dashboard.mail,
//         icon: ICONS.mail,
//         info: (
//           <Label color="error" variant="inverted">
//             +32
//           </Label>
//         ),
//       },
//       { title: 'Chat', path: demoPaths.dashboard.chat, icon: ICONS.chat },
//       { title: 'Calendar', path: demoPaths.dashboard.calendar, icon: ICONS.calendar },
//       { title: 'Kanban', path: demoPaths.dashboard.kanban, icon: ICONS.kanban },
//     ],
//   },
//   /**
//    * Item State
//    */
//   {
//     subheader: 'Misc',
//     items: [
//       {
//         // default roles : All roles can see this entry.
//         // roles: ['user'] Only users can see this item.
//         // roles: ['admin'] Only admin can see this item.
//         // roles: ['admin', 'manager'] Only admin/manager can see this item.
//         // Reference from 'src/guards/RoleBasedGuard'.
//         title: 'Permission',
//         path: demoPaths.dashboard.permission,
//         icon: ICONS.lock,
//         roles: ['admin', 'manager'],
//         caption: 'Only admin can see this item',
//       },
//       {
//         title: 'Level',
//         path: '#/dashboard/menu_level',
//         icon: ICONS.menuItem,
//         children: [
//           {
//             title: 'Level 1a',
//             path: '#/dashboard/menu_level/menu_level_1a',
//             children: [
//               {
//                 title: 'Level 2a',
//                 path: '#/dashboard/menu_level/menu_level_1a/menu_level_2a',
//               },
//               {
//                 title: 'Level 2b',
//                 path: '#/dashboard/menu_level/menu_level_1a/menu_level_2b',
//                 children: [
//                   {
//                     title: 'Level 3a',
//                     path: '#/dashboard/menu_level/menu_level_1a/menu_level_2b/menu_level_3a',
//                   },
//                   {
//                     title: 'Level 3b',
//                     path: '#/dashboard/menu_level/menu_level_1a/menu_level_2b/menu_level_3b',
//                   },
//                 ],
//               },
//             ],
//           },
//           { title: 'Level 1b', path: '#/dashboard/menu_level/menu_level_1b' },
//         ],
//       },
//       {
//         title: 'Disabled',
//         path: '#disabled',
//         icon: ICONS.disabled,
//         disabled: true,
//       },
//       {
//         title: 'Label',
//         path: '#label',
//         icon: ICONS.label,
//         info: (
//           <Label
//             color="info"
//             variant="inverted"
//             startIcon={<Iconify icon="solar:bell-bing-bold-duotone" />}
//           >
//             NEW
//           </Label>
//         ),
//       },
//       {
//         title: 'Caption',
//         path: '#caption',
//         icon: ICONS.menuItem,
//         caption:
//           'Quisque malesuada placerat nisl. In hac habitasse platea dictumst. Cras id dui. Pellentesque commodo eros a enim. Morbi mollis tellus ac sapien.',
//       },
//       {
//         title: 'Params',
//         path: '/dashboard/params?id=e99f09a7-dd88-49d5-b1c8-1daf80c2d7b1',
//         icon: ICONS.parameter,
//       },
//       {
//         title: 'External link',
//         path: 'https://www.google.com/',
//         icon: ICONS.external,
//         info: <Iconify width={18} icon="prime:external-link" />,
//       },
//       { title: 'Blank', path: demoPaths.dashboard.blank, icon: ICONS.blank },
//     ],
//   },
// ];
